import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as SubframeCore from "@subframe/core";
import { DefaultPageLayout } from "../ui/layouts/DefaultPageLayout";
import { DialogLayout } from "../ui/layouts/DialogLayout";
import { Badge } from "../ui/components/Badge";
import { Button } from "../ui/components/Button";
import { IconButton } from "../ui/components/IconButton";
import { Table } from "../ui/components/Table";
import { Loader } from "../ui/components/Loader";
import { Tooltip } from "../ui/components/Tooltip";
import { FeatherArrowLeft } from "@subframe/core";
import { FeatherEdit2 } from "@subframe/core";
import { FeatherTrash } from "@subframe/core";
import { FeatherPlay } from "@subframe/core";
import { FeatherPause } from "@subframe/core";
import { FeatherEye } from "@subframe/core";
import { FeatherAlertTriangle } from "@subframe/core";
import { journalsAPI, JournalTemplate } from "../services/api";
import { useActiveLocations } from "../shared/hooks/useActiveLocations";
import PauseRecurringJournalModal from "../components/PauseRecurringJournalModal";
import ActivityLogComponent from "../components/ActivityLogComponent";
import {
  ReviewRequestModal,
  useToast,
  ReviewStatusBanner,
} from "../shared/presentation/components";
import {
  useJournalReviewData,
  useEntityReviewStatus,
} from "../shared/presentation/hooks";
import { MODULE_CODES, ACTION_CODES } from "../shared/constants/permissions";
import { ChangeDetails } from "../shared/utils/reviewWorkflow";
import { useAuth } from "../features/auth/presentation/hooks/useAuth";

const RecurringJournalDetailedPage: React.FC = () => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();

  // ZOUR-65 F4: names for the GST registration this template files under.
  const {
    locations: activeLocations,
    hasMultiple: hasMultipleLocations,
    primary: primaryLocation,
  } = useActiveLocations();

  /** "Bengaluru Branch · 29AAECN3779L1ZQ (KA)". NULL resolves to the primary. */
  const describeTemplateLocation = (locationId: string | null): string => {
    const match =
      activeLocations.find(
        (candidate) => candidate.zoho_location_id === locationId,
      ) ?? (locationId ? null : primaryLocation);

    if (!match) return "\u2014";

    return `${match.name}${match.gstin ? ` \u00b7 ${match.gstin}` : ""}${
      match.state_code ? ` (${match.state_code})` : ""
    }`;
  };

  // State management
  const [template, setTemplate] = useState<JournalTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Action loading states
  const [pauseLoading, setPauseLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Modal states
  const [pauseModalOpen, setPauseModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  // Review state
  const [reviewChangeDetails, setReviewChangeDetails] = useState<
    ChangeDetails | undefined
  >();

  // Activity log refresh key
  const [activityLogKey, setActivityLogKey] = useState(0);

  // Permission checks using the journal review data hook
  const {
    canCancelRecurringBeforeEnd,
    needsReviewForCancelRecurringBeforeEnd,
    isDataLoading: isPermissionsLoading,
  } = useJournalReviewData({
    moduleCode: MODULE_CODES.JOURNAL,
    entityType: "journal",
    documentId: templateId || null,
    fetchDocument: async (id) => {
      const response = await journalsAPI.getJournalTemplateById(id);
      return response.data;
    },
    isPostedToZoho: () => false, // Templates aren't posted to Zoho
    initialDocument: template,
  });

  // Review status check
  const { review } = useEntityReviewStatus(
    templateId ? "journal_template" : null,
    templateId ? parseInt(templateId) : null,
  );

  // Check if review is pending (blocks all actions)
  const isReviewPending = review?.status === "pending";

  // Disable all actions when under review
  const actionsDisabled = isReviewPending;

  // Fetch template details on mount
  useEffect(() => {
    const fetchTemplateDetails = async () => {
      if (!templateId) {
        setError("No template ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await journalsAPI.getJournalTemplateById(templateId);
        setTemplate(response.data);
      } catch (err) {
        console.error("Error fetching template details:", err);
        setError("Failed to fetch recurring journal template details");
      } finally {
        setLoading(false);
      }
    };

    fetchTemplateDetails();
  }, [templateId]);

  // Helper function to format date
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Helper function to format frequency
  const formatFrequency = (template: JournalTemplate): string => {
    switch (template.recurrence_frequency) {
      case "start_of_month":
        return "Monthly (Start)";
      case "end_of_month":
        return "Monthly (End)";
      case "daily":
        return "Daily";
      case "every_n_days":
        return `Every ${template.recurrence_interval} days`;
      default:
        return template.recurrence_frequency;
    }
  };

  // Helper function to get template status badge
  const getStatusBadge = (template: JournalTemplate) => {
    const isPaused = template.is_paused === "1" || template.is_paused === true;
    const isCancelled =
      template.is_cancelled === "1" || template.is_cancelled === true;

    if (isCancelled) {
      return <Badge variant="error">Ended</Badge>;
    } else if (isPaused) {
      return <Badge variant="warning">Paused</Badge>;
    } else {
      return <Badge variant="success">Active</Badge>;
    }
  };

  // Helper function to format amount
  const formatAmount = (amount: string, currencySymbol?: string): string => {
    const numAmount = parseFloat(amount);
    return `${currencySymbol || "₹"}${numAmount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Helper function to get journal status badge
  const getJournalStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return <Badge variant="success">Approved</Badge>;
      case "under_review":
        return <Badge variant="neutral">Under Review</Badge>;
      case "rejected":
        return <Badge variant="error">Rejected</Badge>;
      default:
        return <Badge variant="neutral">{status}</Badge>;
    }
  };

  // Calculate total amount posted
  const getTotalPosted = (): { amount: string; count: number } => {
    if (!template || !template.generated_journals) {
      return { amount: "0", count: 0 };
    }
    const totalPostedJournals = template.generated_journals.filter(
      (journal) => journal.status === "approved",
    );
    const total = totalPostedJournals.reduce((sum, journal) => {
      return sum + parseFloat(journal.total_debit || "0");
    }, 0);

    return {
      amount: formatAmount(total.toString(), template.currency_symbol),
      count: totalPostedJournals.length,
    };
  };

  // Calculate amount per entry
  const getAmountPerEntry = (): string => {
    if (!template || !template.line_items || template.line_items.length === 0) {
      return "—";
    }

    const totalDebit = template.line_items
      .filter((item) => item.is_debit_or_credit === "debit")
      .reduce((sum, item) => sum + item.amount, 0);

    return formatAmount(totalDebit.toString(), template.currency_symbol);
  };

  // Helper to get end condition text
  const getEndCondition = (template: JournalTemplate): string => {
    // never_expires can be string "1", number 1, or boolean true
    const neverExpires =
      template.never_expires === "1" ||
      template.never_expires === 1 ||
      template.never_expires === true;

    if (neverExpires) {
      return "Never (Manual stop only)";
    }
    if (template.ends_on) {
      return `Until ${formatDate(template.ends_on)}`;
    }
    return "N/A";
  };

  // Handle pause template
  const handlePauseTemplate = async (
    pauseReason: string,
    pauseDuration?: string,
  ) => {
    if (!templateId) return;

    try {
      setPauseLoading(true);
      await journalsAPI.pauseRecurringTemplate(
        templateId,
        pauseReason,
        pauseDuration,
      );

      // Close modal
      setPauseModalOpen(false);

      // Refresh template data
      const response = await journalsAPI.getJournalTemplateById(templateId);
      setTemplate(response.data);

      // Refresh activity log
      setActivityLogKey((prev) => prev + 1);
    } catch (err: any) {
      console.error("Error pausing template:", err);
      alert(err.message || "Failed to pause recurring journal");
    } finally {
      setPauseLoading(false);
    }
  };

  // Handle resume template
  const handleResumeTemplate = async () => {
    if (!templateId) return;

    try {
      setResumeLoading(true);
      await journalsAPI.resumeRecurringTemplate(templateId);

      // Refresh template data
      const response = await journalsAPI.getJournalTemplateById(templateId);
      setTemplate(response.data);

      // Refresh activity log
      setActivityLogKey((prev) => prev + 1);
    } catch (err: any) {
      console.error("Error resuming template:", err);
      alert(err.message || "Failed to resume recurring journal");
    } finally {
      setResumeLoading(false);
    }
  };

  // Helper function to check if cancelling before end date
  const isCancellingBeforeEndDate = (): boolean => {
    if (!template) return false;

    // If never expires, it's always "before end date"
    // never_expires can be string "1", number 1, or boolean true
    const neverExpires =
      template.never_expires === "1" ||
      template.never_expires === 1 ||
      template.never_expires === true;

    if (neverExpires) {
      return true;
    }

    // If has end date, check if we're before it
    if (template.ends_on) {
      const endDate = new Date(template.ends_on);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      return today < endDate;
    }

    return false;
  };

  // Handle cancel template - with permission checks
  const handleCancelTemplate = async () => {
    if (!templateId || !template) return;

    try {
      const isCancellingEarly = isCancellingBeforeEndDate();

      // Check if cancelling before end date requires permission
      if (isCancellingEarly) {
        // Check permission
        if (!canCancelRecurringBeforeEnd) {
          showError(
            "You don't have permission to cancel this recurring journal before its end date. Please contact your manager.",
          );
          setCancelModalOpen(false);
          return;
        }

        // Check if review is required
        if (needsReviewForCancelRecurringBeforeEnd) {
          // Prepare complete change details for review with ALL template information
          const changeDetails: ChangeDetails = {
            action_types: ["cancel_recurring_before_end"],
            raw_json: {
              // Template basic info
              template_id: template.id,
              template_name: template.template_name,
              type: template.type,
              currency_id: template.currency_id,
              currency_symbol: template.currency_symbol,
              notes: template.notes,
              reference_number_prefix: template.reference_number_prefix,

              // Recurrence settings
              recurrence_frequency: template.recurrence_frequency,
              recurrence_interval: template.recurrence_interval,
              starts_on: template.starts_on,
              ends_on: template.ends_on,
              never_expires: template.never_expires,
              next_posting_date: template.next_posting_date,

              // Status info
              is_paused: template.is_paused,
              is_cancelled: template.is_cancelled,
              paused_at: template.paused_at,
              pause_reason: template.pause_reason,
              cancelled_at: template.cancelled_at,
              cancel_reason: template.cancel_reason,

              // Generated journals info
              total_generated_count: template.total_generated_count || 0,
              generated_journals: template.generated_journals || [],

              // Line items
              line_items: template.line_items || [],

              // Timestamps
              created_at: template.created_at,
              updated_at: template.updated_at,
            },
            field_changes: [
              {
                field: "Status",
                old: "Active",
                new: "Cancelled",
              },
              {
                field: "Recurring Journal Name",
                old: "",
                new: template.template_name,
              },
              {
                field: "Frequency",
                old: "",
                new: formatFrequency(template),
              },
              {
                field: "Start Date",
                old: "",
                new: formatDate(template.starts_on),
              },
              {
                field: "End Date",
                old: "",
                new: template.ends_on
                  ? formatDate(template.ends_on)
                  : "Never expires",
              },
              {
                field: "Next Posting Date",
                old: "",
                new: template.next_posting_date
                  ? formatDate(template.next_posting_date)
                  : "N/A",
              },
              {
                field: "Total Journals Generated",
                old: "",
                new: String(template.total_generated_count || 0),
              },
            ],
          };

          setReviewChangeDetails(changeDetails);
          setCancelModalOpen(false);
          setIsReviewModalOpen(true);
          return;
        }
      }

      // If no permission check needed or permission granted without review
      await executeCancelTemplate();
    } catch (err: any) {
      console.error("Error in cancel template flow:", err);
      showError(err.message || "Failed to cancel recurring journal");
      setCancelModalOpen(false);
    }
  };

  // Execute actual cancellation
  const executeCancelTemplate = async () => {
    if (!templateId) return;

    try {
      setCancelLoading(true);
      await journalsAPI.cancelRecurringTemplate(templateId);

      // Close modal
      setCancelModalOpen(false);

      // Refresh template data
      const response = await journalsAPI.getJournalTemplateById(templateId);
      setTemplate(response.data);

      // Refresh activity log
      setActivityLogKey((prev) => prev + 1);

      showSuccess("Recurring journal cancelled successfully");
    } catch (err: any) {
      console.error("Error cancelling template:", err);
      showError(err.message || "Failed to cancel recurring journal");
      setCancelModalOpen(false);
    } finally {
      setCancelLoading(false);
    }
  };

  // Handle review request success
  const handleReviewSuccess = async (comments?: string) => {
    // Review request created successfully
    // Close review modal
    setIsReviewModalOpen(false);

    // Show success message
    showSuccess(
      "Review request submitted successfully. Awaiting approval to cancel recurring journal.",
    );

    // Navigate back to list page
    navigate("/journal/recurring");
  };

  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center">
          <Loader />
        </div>
      </DefaultPageLayout>
    );
  }

  if (error || !template) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full flex-col items-center justify-center gap-4">
          <span className="text-body font-body text-error-600">
            {error || "Template not found"}
          </span>
          <Button onClick={() => navigate("/journal/recurring")}>
            Back to Recurring Journals
          </Button>
        </div>
      </DefaultPageLayout>
    );
  }

  const isPaused = template.is_paused === "1" || template.is_paused === true;
  const isCancelled =
    template.is_cancelled === "1" || template.is_cancelled === true;
  const totalPosted = getTotalPosted();

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start bg-default-background">
        {/* Header */}
        <div className="flex w-full flex-wrap items-start justify-between border-b border-solid border-neutral-border px-8 py-6">
          <div className="flex flex-wrap items-center justify-between">
            <IconButton
              icon={<FeatherArrowLeft />}
              onClick={() => navigate("/journal/recurring")}
            />
            <span className="text-body font-body text-default-font ml-2">
              Back to Recurring Journals
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* <Button
              variant="neutral-tertiary"
              icon={<FeatherDownload />}
              onClick={() => {
                // TODO: Export functionality
                console.log("Export template:", templateId);
              }}
            >
              Export
            </Button> */}
            {template.is_cancelled === "0" && (
              <Button
                variant="neutral-secondary"
                icon={<FeatherEdit2 />}
                onClick={() =>
                  navigate(`/journal/recurring/${templateId}/edit`)
                }
                disabled={actionsDisabled}
              >
                Edit
              </Button>
            )}
            {!isCancelled && (
              <>
                {isPaused ? (
                  <Button
                    className="[&>div]:!text-success-700 [&>span]:!text-success-700 bg-success-100 hover:bg-success-200"
                    variant="brand-secondary"
                    icon={resumeLoading ? null : <FeatherPlay />}
                    onClick={handleResumeTemplate}
                    disabled={resumeLoading || actionsDisabled}
                  >
                    {resumeLoading ? (
                      <div className="flex items-center gap-2">
                        <Loader size="small" />
                        <span>Resuming...</span>
                      </div>
                    ) : (
                      "Resume"
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="neutral-secondary"
                    icon={<FeatherPause />}
                    onClick={() => setPauseModalOpen(true)}
                    disabled={actionsDisabled}
                  >
                    Pause
                  </Button>
                )}
              </>
            )}
            {template.is_cancelled === "0" && (
              <Button
                variant="destructive-secondary"
                icon={<FeatherTrash />}
                onClick={() => setCancelModalOpen(true)}
                disabled={
                  actionsDisabled ||
                  isPermissionsLoading ||
                  (isCancellingBeforeEndDate() && !canCancelRecurringBeforeEnd)
                }
              >
                {isPermissionsLoading
                  ? "Cancel"
                  : isCancellingBeforeEndDate() && !canCancelRecurringBeforeEnd
                    ? "Cancel (No Permission)"
                    : "Cancel"}
              </Button>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 px-8 py-8 overflow-auto">
          {/* Review Status Banner - Show when review exists and is pending/rejected */}
          {(() => {
            const shouldShow =
              review &&
              (review.status === "pending" || review.status === "rejected");
            console.log("🎨 Banner Render Check:", {
              hasReview: !!review,
              reviewStatus: review?.status,
              reviewActionCode: review?.action_code,
              isPending: review?.status === "pending",
              isRejected: review?.status === "rejected",
              shouldShow: shouldShow,
              reviewObject: review,
            });

            // FORCE SHOW FOR TESTING (remove after debugging)
            const FORCE_SHOW_BANNER = false; // Set to true to test banner display

            if (FORCE_SHOW_BANNER && review) {
              console.log("⚠️ FORCE SHOWING BANNER FOR TESTING");
              return (
                <div className="w-full">
                  <ReviewStatusBanner
                    review={review}
                    moduleCode="journal"
                    currentUserId={user?.id}
                  />
                </div>
              );
            }

            return (
              shouldShow && (
                <div className="w-full">
                  <ReviewStatusBanner
                    review={review}
                    moduleCode="journal"
                    currentUserId={user?.id}
                    onResubmit={
                      review.status === "rejected"
                        ? () => {
                            setCancelModalOpen(true);
                          }
                        : undefined
                    }
                  />
                </div>
              )
            );
          })()}

          {/* Title */}
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start gap-2">
              <div className="flex items-center gap-3">
                <span className="text-heading-1 font-heading-1 text-default-font">
                  {template.template_name}
                </span>
                {getStatusBadge(template)}
              </div>
              <span className="text-body font-body text-subtext-color">
                Created on {formatDate(template.created_at || null)}
              </span>
            </div>
          </div>

          {/* Main content grid */}
          <div className="flex w-full items-start gap-6">
            {/* Left column - Basic Information */}
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-6">
              <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Basic Information
                </span>
                <div className="w-full items-start gap-6 grid grid-cols-2">
                  <div className="flex flex-col items-start gap-2">
                    <span className="text-body font-body text-subtext-color">
                      Recurring ID
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      RJ-{template.id}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2">
                    <span className="text-body font-body text-subtext-color">
                      Recurring Name
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {template.template_name}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2">
                    <span className="text-body font-body text-subtext-color">
                      Frequency
                    </span>
                    <Badge variant="neutral">{formatFrequency(template)}</Badge>
                  </div>
                  <div className="flex flex-col items-start gap-2">
                    <span className="text-body font-body text-subtext-color">
                      Start Date
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {formatDate(template.starts_on)}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2">
                    <span className="text-body font-body text-subtext-color">
                      Next Posting Date
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {formatDate(template.next_posting_date || null)}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2">
                    <span className="text-body font-body text-subtext-color">
                      End Date
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {getEndCondition(template)}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2">
                    <span className="text-body font-body text-subtext-color">
                      Created On
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {formatDate(template.created_at || null)}
                    </span>
                  </div>
                  <div className="flex flex-col items-start gap-2">
                    <span className="text-body font-body text-subtext-color">
                      Reference Prefix
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {template.reference_number_prefix || "N/A"}
                    </span>
                  </div>
                  {/* ZOUR-65 F4: the registration every journal this template
                      mints is filed under. Only where there is a choice. */}
                  {hasMultipleLocations && (
                    <div className="flex flex-col items-start gap-2">
                      <span className="text-body font-body text-subtext-color">
                        Location
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {describeTemplateLocation(template.location_id ?? null)}
                      </span>
                    </div>
                  )}
                  {template.notes && (
                    <div className="flex flex-col items-start gap-2 col-span-2">
                      <span className="text-body font-body text-subtext-color">
                        Notes
                      </span>
                      <span className="text-body-bold font-body-bold text-default-font">
                        {template.notes}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right column - Summary */}
            <div className="flex w-96 flex-none flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Summary
              </span>
              <div className="flex w-full flex-col items-start gap-3">
                <div className="flex w-full items-center justify-between pt-3">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Status
                  </span>
                  {getStatusBadge(template)}
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Frequency
                  </span>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {formatFrequency(template)}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Amount per entry
                  </span>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {getAmountPerEntry()}
                  </span>
                </div>
                <div className="flex w-full items-center justify-between">
                  <span className="text-body font-body text-subtext-color">
                    Total posted to date
                  </span>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {totalPosted.amount} ({totalPosted.count} entries)
                  </span>
                </div>
                {isPaused && template.paused_at && (
                  <div className="flex w-full items-center justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Paused On
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {formatDate(template.paused_at)}
                    </span>
                  </div>
                )}
                {template.last_processed_date && (
                  <div className="flex w-full items-center justify-between">
                    <span className="text-body font-body text-subtext-color">
                      Last Posted On
                    </span>
                    <span className="text-body-bold font-body-bold text-default-font">
                      {formatDate(template.last_processed_date)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Line Items Section */}
          {template.line_items && template.line_items.length > 0 && (
            <div className="flex w-full flex-col items-start gap-6">
              <div className="flex items-center gap-4">
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Line Items
                </span>
                <Badge variant="neutral">
                  {template.line_items.length} Items
                </Badge>
              </div>
              <Table
                header={
                  <Table.HeaderRow>
                    <Table.HeaderCell>Account</Table.HeaderCell>
                    <Table.HeaderCell>Description</Table.HeaderCell>
                    <Table.HeaderCell>Contact</Table.HeaderCell>
                    <Table.HeaderCell>Debits</Table.HeaderCell>
                    <Table.HeaderCell>Credits</Table.HeaderCell>
                  </Table.HeaderRow>
                }
              >
                {template.line_items.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      <SubframeCore.Tooltip.Provider>
                        <SubframeCore.Tooltip.Root>
                          <SubframeCore.Tooltip.Trigger asChild={true}>
                            <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                              {item.account_name}
                            </span>
                          </SubframeCore.Tooltip.Trigger>
                          <SubframeCore.Tooltip.Portal>
                            <SubframeCore.Tooltip.Content
                              side="top"
                              align="start"
                              sideOffset={4}
                              asChild={true}
                            >
                              <Tooltip>{item.account_name}</Tooltip>
                            </SubframeCore.Tooltip.Content>
                          </SubframeCore.Tooltip.Portal>
                        </SubframeCore.Tooltip.Root>
                      </SubframeCore.Tooltip.Provider>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="whitespace-nowrap text-body font-body text-default-font">
                        {item.description || "—"}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="whitespace-nowrap text-body font-body text-default-font">
                        {item.contact_name || "—"}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="whitespace-nowrap text-body font-body text-default-font">
                        {item.is_debit_or_credit === "debit"
                          ? `${
                              template.currency_symbol
                            }${item.amount.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`
                          : "—"}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="whitespace-nowrap text-body font-body text-default-font">
                        {item.is_debit_or_credit === "credit"
                          ? `${
                              template.currency_symbol
                            }${item.amount.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`
                          : "—"}
                      </span>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table>
            </div>
          )}

          {/* Generated Journal Entries Section */}
          {template.generated_journals &&
            template.generated_journals.length > 0 && (
              <div className="flex w-full flex-col items-start gap-6">
                <div className="flex items-center gap-4">
                  <span className="text-heading-2 font-heading-2 text-default-font">
                    Generated Journal Entries
                  </span>
                  <Badge variant="neutral">
                    {template.generated_journals.length} Entries
                  </Badge>
                </div>
                <Table
                  header={
                    <Table.HeaderRow>
                      <Table.HeaderCell>Journal Entry ID</Table.HeaderCell>
                      <Table.HeaderCell>Date</Table.HeaderCell>
                      <Table.HeaderCell>Status</Table.HeaderCell>
                      <Table.HeaderCell>Amount</Table.HeaderCell>
                      <Table.HeaderCell>Actions</Table.HeaderCell>
                    </Table.HeaderRow>
                  }
                >
                  {template.generated_journals.map((journal) => (
                    <Table.Row key={journal.id}>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                          {journal.id}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body font-body text-default-font">
                          {formatDate(journal.date)}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        {getJournalStatusBadge(journal.status)}
                      </Table.Cell>
                      <Table.Cell>
                        <span className="whitespace-nowrap text-body font-body text-default-font">
                          {formatAmount(
                            journal.total_debit,
                            template.currency_symbol,
                          )}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          variant="neutral-tertiary"
                          size="small"
                          icon={<FeatherEye />}
                          onClick={() =>
                            navigate(`/journal/detail/${journal.id}`)
                          }
                        >
                          View
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table>
              </div>
            )}

          {/* Activity Log */}
          {templateId && (
            <ActivityLogComponent
              key={activityLogKey}
              feature="journal_templates"
              featureId={templateId}
            />
          )}
        </div>
      </div>

      {/* Pause Modal */}
      <PauseRecurringJournalModal
        open={pauseModalOpen}
        onOpenChange={setPauseModalOpen}
        onSubmit={handlePauseTemplate}
        loading={pauseLoading}
        recurrenceFrequency={template?.recurrence_frequency}
      />

      {/* Cancel Confirmation Modal */}
      <DialogLayout
        open={cancelModalOpen}
        onOpenChange={(open: boolean) => {
          if (!cancelLoading) {
            setCancelModalOpen(open);
          }
        }}
      >
        <div className="flex w-[480px] flex-col items-start gap-6 bg-default-background px-8 py-6">
          <div className="flex w-full items-center gap-4">
            <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-error-100">
              <FeatherAlertTriangle className="text-error-600" />
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Cancel Recurring Journal
              </span>
              <span className="text-body font-body text-subtext-color">
                Are you sure you want to cancel this recurring journal?
              </span>
            </div>
          </div>

          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              This action will:
            </span>
            <ul className="list-disc list-inside text-body font-body text-subtext-color space-y-1">
              <li>Stop all future journal entries from being generated</li>
              <li>Mark this template as cancelled</li>
              <li>Existing journal entries will remain unchanged</li>
            </ul>
            {isCancellingBeforeEndDate() && (
              <div className="flex w-full flex-col items-start gap-2 rounded-md bg-warning-50 px-4 py-3 border border-solid border-warning-200 mt-2">
                <span className="text-body-bold font-body-bold text-warning-700">
                  Cancelling Before End Date
                </span>
                <span className="text-body font-body text-warning-700">
                  {template?.never_expires === "1" ||
                  template?.never_expires === 1 ||
                  template?.never_expires === true
                    ? "This recurring journal is set to never expire."
                    : `This recurring journal is scheduled to run until ${formatDate(
                        template?.ends_on,
                      )}.`}
                  {needsReviewForCancelRecurringBeforeEnd &&
                    " Manager approval is required to cancel it early."}
                </span>
              </div>
            )}
            <span className="text-body-bold font-body-bold text-error-600 mt-2">
              This action cannot be undone.
            </span>
          </div>

          <div className="flex w-full items-center justify-end gap-2">
            <Button
              variant="neutral-tertiary"
              onClick={() => setCancelModalOpen(false)}
              disabled={cancelLoading}
            >
              Go Back
            </Button>
            <Button
              variant="destructive-primary"
              onClick={handleCancelTemplate}
              disabled={cancelLoading}
              icon={cancelLoading ? null : <FeatherTrash />}
            >
              {cancelLoading ? (
                <div className="flex items-center gap-2">
                  <Loader size="small" />
                  <span>Cancelling...</span>
                </div>
              ) : needsReviewForCancelRecurringBeforeEnd &&
                isCancellingBeforeEndDate() ? (
                "Submit for Review"
              ) : (
                "Yes, Cancel Recurring Journal"
              )}
            </Button>
          </div>
        </div>
      </DialogLayout>

      {/* Review Request Modal for Cancellation */}
      <ReviewRequestModal
        open={isReviewModalOpen}
        onOpenChange={setIsReviewModalOpen}
        moduleCode={MODULE_CODES.JOURNAL}
        actionCode={ACTION_CODES.CANCEL_RECURRING_BEFORE_END}
        entityType="journal_template"
        entityId={parseInt(templateId || "0")}
        documentNumber={template?.template_name}
        changeDetails={reviewChangeDetails}
        onSuccess={handleReviewSuccess}
        skipNavigation={true}
        title="Request to Cancel Recurring Journal"
      />
    </DefaultPageLayout>
  );
};

export default RecurringJournalDetailedPage;
